import { Controller } from "@hotwired/stimulus"

// Workshop application form. Progressive enhancement: the <form> is a real
// POST that works with JS off (the Worker 302s to the /gracias/ page). With JS
// we validate, submit via fetch, and swap in an inline confirmation without a
// full navigation. Also reveals the conditional "describe your project" field
// and renders Cloudflare Turnstile when a site key is configured.
const TURNSTILE_SRC =
  "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit"

export default class extends Controller {
  static targets = ["projectDetail", "status", "submit", "turnstile"]
  // The confirmation copy comes from `confirmacion:` in punto_partida.yml via
  // data attributes, so this panel and the no-JS /gracias/ page always say the
  // same thing. The literals below are only a fallback for a form rendered
  // without them.
  static values = {
    turnstileSitekey: String,
    doneEyebrowEs: String,
    doneEyebrowEn: String,
    doneTitleEs: String,
    doneTitleEn: String,
    doneBodyEs: String,
    doneBodyEn: String,
  }

  connect() {
    this.submitting = false
    this.widgetId = null
    this.renderTurnstile()
  }

  disconnect() {
    // Turnstile leaves nodes behind across Turbo navigations; clean up.
    if (this.widgetId != null && window.turnstile) {
      try { window.turnstile.remove(this.widgetId) } catch (_) { /* ignore */ }
      this.widgetId = null
    }
  }

  // Show the project-description field only when "Sí" is selected. Toggle its
  // `required` in step with visibility — a hidden required field is unfocusable
  // and would make native validation throw.
  toggleProject(event) {
    if (!this.hasProjectDetailTarget) return
    const wants = event.target.value === "si" && event.target.checked
    this.projectDetailTarget.hidden = !wants
    const field = this.projectDetailTarget.querySelector("textarea, input")
    if (field) field.required = wants
  }

  async submit(event) {
    event.preventDefault()
    if (this.submitting) return

    const form = this.element
    if (!form.checkValidity()) {
      form.reportValidity()
      return
    }

    this.setSubmitting(true)
    this.setStatus("", null)

    try {
      const response = await fetch(form.action, {
        method: "POST",
        body: new FormData(form),
        headers: { Accept: "application/json" },
      })

      if (response.ok) {
        this.showSuccess()
        return
      }

      let message = null
      try {
        const data = await response.json()
        message = data && data.error
      } catch (_) { /* non-JSON error body */ }
      this.setStatus(
        message ||
          "No se pudo enviar. Revisa los campos e inténtalo de nuevo.",
        "error"
      )
      this.resetTurnstile()
    } catch (_) {
      this.setStatus(
        "Hubo un problema de conexión. Inténtalo de nuevo.",
        "error"
      )
      this.resetTurnstile()
    } finally {
      if (!this.done) this.setSubmitting(false)
    }
  }

  // Replace the form — and the "Postúlate" heading above it — with a
  // confirmation that mirrors the no-JS /gracias/ page, so a JS submission is
  // just as unmistakable as a native one.
  showSuccess() {
    this.done = true

    const panel = document.createElement("div")
    panel.className = "f-done"
    panel.setAttribute("role", "status")
    panel.setAttribute("tabindex", "-1")

    const eyebrow = this.bilingual(
      "p",
      this.doneEyebrowEsValue || "Postulación recibida",
      this.doneEyebrowEnValue || "Application received"
    )
    eyebrow.className = "f-done-eyebrow"

    const title = this.bilingual(
      "h2",
      this.doneTitleEsValue || "Recibí tu postulación.",
      this.doneTitleEnValue || "I received your application."
    )
    title.className = "f-done-title"

    const body = this.bilingual(
      "p",
      this.doneBodyEsValue ||
        "Gracias por tu interés. Revisaré las solicitudes y te contactaré por correo. El cupo es limitado a 12 personas.",
      this.doneBodyEnValue ||
        "Thank you for your interest. I will review the applications and contact you by email. Capacity is limited to 12 people."
    )
    body.className = "f-done-body"

    panel.append(eyebrow, title, body)

    // Hide the section intro (heading, deadline, "all fields required") so the
    // confirmation isn't sitting under a call to apply.
    let node = this.element.previousElementSibling
    while (node) {
      node.hidden = true
      node = node.previousElementSibling
    }

    this.element.replaceWith(panel)
    panel.scrollIntoView({ behavior: "smooth", block: "center" })
    panel.focus({ preventScroll: true })
  }

  // Build an element carrying both languages, rendered in the active one.
  // `data-es` / `data-en` keep the site-wide language toggle working.
  bilingual(tag, es, en) {
    const el = document.createElement(tag)
    el.dataset.es = es
    el.dataset.en = en
    el.textContent = document.documentElement.lang === "en" ? en : es
    return el
  }

  renderTurnstile() {
    const key = this.turnstileSitekeyValue
    if (!key || !this.hasTurnstileTarget) return

    const mount = () => {
      if (!window.turnstile || this.widgetId != null) return
      this.widgetId = window.turnstile.render(this.turnstileTarget, {
        sitekey: key,
        theme: "light",
      })
    }

    if (window.turnstile) {
      mount()
      return
    }
    if (!document.querySelector(`script[src^="${TURNSTILE_SRC.split("?")[0]}"]`)) {
      const script = document.createElement("script")
      script.src = TURNSTILE_SRC
      script.async = true
      script.defer = true
      script.addEventListener("load", mount)
      document.head.appendChild(script)
    } else {
      // Script already present but not ready yet; poll briefly.
      const t = setInterval(() => {
        if (window.turnstile) { clearInterval(t); mount() }
      }, 150)
      setTimeout(() => clearInterval(t), 5000)
    }
  }

  resetTurnstile() {
    if (this.widgetId != null && window.turnstile) {
      try { window.turnstile.reset(this.widgetId) } catch (_) { /* ignore */ }
    }
  }

  setSubmitting(on) {
    this.submitting = on
    if (this.hasSubmitTarget) this.submitTarget.disabled = on
  }

  setStatus(message, state) {
    if (!this.hasStatusTarget) return
    this.statusTarget.textContent = message
    if (state) this.statusTarget.setAttribute("data-state", state)
    else this.statusTarget.removeAttribute("data-state")
  }
}
